import * as pc from "playcanvas";
import type { GameClient } from "../net/GameClient";
import { resumeAudioContext, unlockAudioOnGesture } from "./audioContext";

/**
 * PlayerSounds — body reactions for the LOCAL player only (2D):
 *   • death     — when the local player is eliminated, one of 3 death
 *                 grunts plays at random. Only the player who died hears it.
 *   • burn pain — while the local player stands in fire, one of 3 pain
 *                 grunts plays at random, throttled so the 0.25 s burn ticks
 *                 don't spam it.
 */

const DEATH_URLS = ["/audio/death1.mp3", "/audio/death2.mp3", "/audio/death3.mp3"];
const BURN_URLS = ["/audio/burn-pain1.mp3", "/audio/burn-pain2.mp3", "/audio/burn-pain3.mp3"];
const ALL_URLS = [...DEATH_URLS, ...BURN_URLS];

// Death — 2D; only the eliminated player hears it.
const DEATH_VOLUME = 0.85;

// Burn pain — local 2D, throttled (fire damage ticks every 0.25 s).
const BURN_VOLUME = 0.6;
const BURN_THROTTLE_MS = 700;
/** Fire damage reports this weaponId regardless of molotov vs incendiary. */
const FIRE_WEAPONS = new Set(["molotov", "incendiary"]);

const slotName = (url: string): string => url.replace(/[^a-z0-9]/gi, "_");

export class PlayerSounds {
  private readonly app: pc.AppBase;
  private readonly client: GameClient;
  private readonly localEmitter: pc.Entity;
  private readonly assets = new Map<string, pc.Asset>();
  private ready = false;
  private readonly unsubs: Array<() => void> = [];
  private lastBurnAt = -1e9;
  private lastDeathIdx = -1;
  private lastBurnIdx = -1;

  constructor(app: pc.AppBase, client: GameClient, camera: pc.Entity) {
    this.app = app;
    this.client = client;

    if (!camera.audiolistener) camera.addComponent("audiolistener");
    this.localEmitter = new pc.Entity("player-snd-local");
    camera.addChild(this.localEmitter);

    this.loadAssets();
    unlockAudioOnGesture(this.app);

    this.unsubs.push(
      this.client.onHit((msg) => {
        // Death — ONLY the eliminated player hears their own death grunt.
        if (msg.victimHealthAfter <= 0) {
          if (msg.victimSessionId === this.client.sessionId) this.playDeath();
          return; // the lethal burn tick plays a death grunt, not a pain grunt
        }
        // Burn pain — local player taking fire damage, throttled.
        if (FIRE_WEAPONS.has(msg.weaponId) && msg.victimSessionId === this.client.sessionId) {
          const now = performance.now();
          if (now - this.lastBurnAt >= BURN_THROTTLE_MS) {
            this.lastBurnAt = now;
            this.playBurn();
          }
        }
      }),
    );
  }

  destroy(): void {
    for (const u of this.unsubs) u();
    this.localEmitter.destroy();
  }

  /** Pick a random url, avoiding an immediate repeat. */
  private pick(urls: string[], lastIdx: number): { url: string; idx: number } {
    let idx = Math.floor(Math.random() * urls.length);
    if (urls.length > 1 && idx === lastIdx) idx = (idx + 1) % urls.length;
    return { url: urls[idx], idx };
  }

  private playBurn(): void {
    if (!this.ready) return;
    resumeAudioContext(this.app);
    const { url, idx } = this.pick(BURN_URLS, this.lastBurnIdx);
    this.lastBurnIdx = idx;
    this.localEmitter.sound?.play(slotName(url));
  }

  private playDeath(): void {
    if (!this.ready) return;
    resumeAudioContext(this.app);
    const { url, idx } = this.pick(DEATH_URLS, this.lastDeathIdx);
    this.lastDeathIdx = idx;
    this.localEmitter.sound?.play(slotName(url));
  }

  private loadAssets(): void {
    let remaining = ALL_URLS.length;
    for (const url of ALL_URLS) {
      this.app.assets.loadFromUrl(url, "audio", (err, asset) => {
        if (err || !asset) console.warn("[PlayerSounds] failed to load:", url, err);
        else this.assets.set(url, asset);
        if (--remaining === 0) this.onAssetsReady();
      });
    }
  }

  private onAssetsReady(): void {
    // All clips (death + burn pain) live on the local 2D emitter — both are
    // heard only by the local player.
    this.localEmitter.addComponent("sound");
    const s = this.localEmitter.sound;
    if (s) {
      s.positional = false;
      for (const url of ALL_URLS) {
        const asset = this.assets.get(url);
        if (!asset) continue;
        const volume = DEATH_URLS.includes(url) ? DEATH_VOLUME : BURN_VOLUME;
        s.addSlot(slotName(url), { asset: asset.id, volume, loop: false, autoPlay: false, overlap: true });
      }
    }
    this.ready = true;
  }
}
